import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

import {
  useAddTodoMutation,
  useGetTodosQuery,
  useDeleteTodoMutation,
} from "../../redux/slices/productApiSlice";

import {
  useAddsTodoMutation,
  useGetsTodosQuery,
  useDeleteeTodoMutation,
} from "../../redux/slices/todoApiSlice";

import { useNavigate } from "react-router-dom";

import {
  FaRegHeart,
  FaHeart,
  FaExclamationTriangle,
  FaShoppingCart,
  FaSyncAlt,
  FaCheckCircle,
  FaTrash,
} from "react-icons/fa";

import { toast } from "react-toastify";

const Catos = ({ catagory }) => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // CART
  // =========================================================

  const [addingProductId, setAddingProductId] = useState(null);

  const [addTodo] = useAddTodoMutation();
  const [deleteTodo] = useDeleteTodoMutation();

  const { data: cartData } = useGetTodosQuery();

  // =========================================================
  // FAVORITE
  // =========================================================

  const [addingFavoriteId, setAddingFavoriteId] = useState(null);

  const [addsTodo] = useAddsTodoMutation();
  const [deleteeTodo] = useDeleteeTodoMutation();

  const { data: favoriteData } = useGetsTodosQuery();

  const [favoriteIds, setFavoriteIds] = useState([]);

  const navigate = useNavigate();

  const { userInfo } = useSelector(
    (state) => state.auth
  );

  // =========================================================
  // ERROR MESSAGE
  // =========================================================

  const getErrorMessage = (err) => {
    const status =
      err?.response?.status ||
      err?.status;

    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.data?.message ||
      err?.data?.message ||
      err?.data?.error ||
      err?.message;

    if (serverMessage) {
      if (typeof serverMessage === "string") {
        return serverMessage;
      }

      try {
        return JSON.stringify(serverMessage);
      } catch {
        return "Serverdən naməlum xəta cavabı alındı.";
      }
    }

    if (status === 400) {
      return "Göndərilən məlumatlar düzgün deyil.";
    }

    if (status === 401) {
      return "Bu əməliyyatı yerinə yetirmək üçün hesabınıza daxil olmalısınız.";
    }

    if (status === 403) {
      return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
    }

    if (status === 404) {
      return "Sorğu edilən məlumat tapılmadı.";
    }

    if (status === 409) {
      return "Bu məhsul artıq mövcuddur.";
    }

    if (status === 429) {
      return "Çox sayda sorğu göndərildi. Bir neçə saniyə gözləyib yenidən cəhd edin.";
    }

    if (status >= 500) {
      return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
    }

    if (
      err?.code === "ERR_NETWORK" ||
      err?.message
        ?.toLowerCase()
        ?.includes("network")
    ) {
      return "Serverlə əlaqə yaratmaq mümkün olmadı. İnternet bağlantınızı yoxlayın.";
    }

    return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
  };

  // =========================================================
  // DUPLICATE FAVORITE CHECK
  // =========================================================

  const isAlreadyFavoriteError = (err) => {
    const status =
      err?.response?.status ||
      err?.status;

    const message = String(
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.data?.message ||
      err?.data?.error ||
      err?.message ||
      ""
    ).toLowerCase();

    return (
      status === 409 ||
      message.includes("already") ||
      message.includes("exist") ||
      message.includes("duplicate") ||
      message.includes("artıq") ||
      message.includes("artiq") ||
      message.includes("mövcuddur") ||
      message.includes("movcuddur") ||
      message.includes("favor") ||
      message.includes("sevimli")
    );
  };

  // =========================================================
  // CART DATA
  // =========================================================

  const cartItems =
    cartData?.todos ||
    cartData?.allTodos ||
    cartData?.products ||
    cartData ||
    [];

  const getCartProductId = (item) => {
    const productId =
      item?.productId?._id ||
      item?.productId ||
      item?.product?._id ||
      item?.qolbaq?._id ||
      item?.qolbaqId?._id ||
      item?.qolbaqId ||
      item?.qolbaq_id;

    return productId
      ? String(productId)
      : null;
  };

  const isProductInCart = (productId) => {
    if (
      !Array.isArray(cartItems) ||
      !productId
    ) {
      return false;
    }

    return cartItems.some(
      (item) =>
        getCartProductId(item) ===
        String(productId)
    );
  };

  const getCartItem = (productId) => {
    if (
      !Array.isArray(cartItems) ||
      !productId
    ) {
      return null;
    }

    return (
      cartItems.find(
        (item) =>
          getCartProductId(item) ===
          String(productId)
      ) || null
    );
  };

  // =========================================================
  // FAVORITE DATA -> FAVORITE IDS
  // =========================================================

  useEffect(() => {
    if (!Array.isArray(favoriteData)) {
      return;
    }

    const ids = favoriteData
      .map((item) => {
        return (
          item?.productId?._id ||
          item?.productId ||
          item?.product?._id ||
          item?.qolbaq?._id ||
          item?.qolbaqId?._id
        );
      })
      .filter(Boolean)
      .map((id) => String(id));

    setFavoriteIds(ids);
  }, [favoriteData]);

  // =========================================================
  // ADD / REMOVE CART
  // =========================================================

  const handleAddToCart = async (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );
      return;
    }

    const productId = String(product._id);
    const productTitle =
      product?.title || "Məhsul";

    const isInCart =
      isProductInCart(productId);

    // Eyni anda başqa məhsula əməliyyat gedirsə
    if (addingProductId !== null) {
      return;
    }

    // =======================================================
    // MƏHSUL ARTİQ SƏBƏTDƏDİR -> SİL
    // =======================================================

    if (isInCart) {
      const cartItem =
        getCartItem(productId);

      if (!cartItem?._id) {
        toast.error(
          `"${productTitle}" məhsulunun səbət məlumatı tapılmadı. Məhsul səbətdən silinə bilmədi.`
        );

        return;
      }

      try {
        setAddingProductId(productId);

        await deleteTodo(
          cartItem._id
        ).unwrap();

        toast.success(
          `"${productTitle}" məhsulu səbətdən silindi.`
        );
      } catch (err) {
        console.error(
          "Məhsul səbətdən silinərkən xəta:",
          err
        );

        toast.error(
          `"${productTitle}" məhsulunu səbətdən silmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      } finally {
        setAddingProductId(null);
      }

      return;
    }

    // =======================================================
    // STOK YOXDUR
    // =======================================================

    if (Number(product.stock) <= 0) {
      toast.warning(
        `"${productTitle}" hazırda stokda olmadığı üçün səbətə əlavə edilə bilməz.`
      );

      return;
    }

    // =======================================================
    // SƏBƏTƏ ƏLAVƏ ET
    // =======================================================

    try {
      setAddingProductId(productId);

      await addTodo({
        productId,
      }).unwrap();

      toast.success(
        `"${productTitle}" məhsulu səbətə uğurla əlavə edildi.`
      );
    } catch (err) {
      console.error(
        "Məhsul səbətə əlavə edilərkən xəta:",
        err
      );

      toast.error(
        `"${productTitle}" məhsulunu səbətə əlavə etmək mümkün olmadı. Səbəb: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setAddingProductId(null);
    }
  };

  // =========================================================
  // ADD / REMOVE FAVORITE
  // =========================================================

  const handleAddToFavorite = async (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );

      return;
    }

    const productId = String(product._id);
    const productTitle =
      product?.title || "Məhsul";

    const isFavorite =
      favoriteIds.includes(productId);

    // Başqa favori əməliyyatı gedirsə
    if (addingFavoriteId !== null) {
      return;
    }

    // =======================================================
    // FAVORİDƏDİR -> SİL
    // =======================================================

    if (isFavorite) {
      const favoriteItem =
        Array.isArray(favoriteData)
          ? favoriteData.find(
            (item) => {
              const id =
                item?.productId?._id ||
                item?.productId ||
                item?.product?._id ||
                item?.qolbaq?._id ||
                item?.qolbaqId?._id;

              return (
                String(id) ===
                productId
              );
            }
          )
          : null;

      if (!favoriteItem?._id) {
        toast.error(
          `"${productTitle}" məhsulunun favori məlumatı tapılmadı. Favoridən silmək mümkün olmadı.`
        );

        return;
      }

      try {
        setAddingFavoriteId(productId);

        await deleteeTodo(
          favoriteItem._id
        ).unwrap();

        // UI-ni dərhal dəyiş
        setFavoriteIds((prev) =>
          prev.filter(
            (id) =>
              String(id) !==
              productId
          )
        );

        toast.success(
          `"${productTitle}" məhsulu sevimlilərdən silindi.`
        );
      } catch (err) {
        console.error(
          "Məhsul sevimlilərdən silinərkən xəta:",
          err
        );

        toast.error(
          `"${productTitle}" məhsulunu sevimlilərdən silmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      } finally {
        setAddingFavoriteId(null);
      }

      return;
    }

    // =======================================================
    // FAVORİYƏ ƏLAVƏ ET
    // =======================================================

    try {
      setAddingFavoriteId(productId);

      await addsTodo({
        productId,
      }).unwrap();

      // UI-ni dərhal dəyiş
      setFavoriteIds((prev) => {
        if (
          prev.includes(productId)
        ) {
          return prev;
        }

        return [
          ...prev,
          productId,
        ];
      });

      toast.success(
        `"${productTitle}" məhsulu sevimlilər siyahısına əlavə edildi.`
      );
    } catch (err) {
      console.error(
        "Məhsul sevimlilərə əlavə edilərkən xəta:",
        err
      );

      if (
        isAlreadyFavoriteError(err)
      ) {
        // Backend artıq var deyirsə,
        // ürəyi aktiv saxla
        setFavoriteIds((prev) => {
          if (
            prev.includes(productId)
          ) {
            return prev;
          }

          return [
            ...prev,
            productId,
          ];
        });

        // Burada artıq ayrıca "artıq favoridədir"
        // toast çıxarmırıq.
      } else {
        toast.error(
          `"${productTitle}" məhsulunu sevimlilərə əlavə etmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      }
    } finally {
      setAddingFavoriteId(null);
    }
  };

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        setError("");

        let url =
          `${process.env.REACT_APP_API_BASE_URL}/api/qolbaq`;

        if (userInfo?._id) {
          url += `/${userInfo._id}?page=${page}`;
        } else {
          url += `?page=${page}`;
        }

        const response =
          await axios.get(url);

        if (!isMounted) {
          return;
        }

        const data =
          response?.data?.allQolbaq;

        if (!Array.isArray(data)) {
          setError(
            "Məhsullar serverdən düzgün formatda qəbul edilmədi."
          );

          setHasMore(false);

          return;
        }

        const filteredProducts =
          data.filter(
            (item) =>
              item.catagory ===
              catagory
          );

        setProducts((prev) => {
          const existingIds =
            new Set(
              prev.map(
                (item) =>
                  item._id
              )
            );

          const newProducts =
            filteredProducts.filter(
              (item) =>
                !existingIds.has(
                  item._id
                )
            );

          return [
            ...prev,
            ...newProducts,
          ];
        });

        if (
          typeof response?.data
            ?.totalPages ===
          "number"
        ) {
          setHasMore(
            page <
            response.data
              .totalPages
          );
        } else {
          setHasMore(
            data.length > 0
          );
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error(
          "Məhsullar yüklənərkən xəta:",
          err
        );

        setError(
          getErrorMessage(err)
        );

        if (page === 1) {
          setHasMore(false);
        }
      } finally {
        if (!isMounted) {
          return;
        }

        setLoading(false);
        setLoadingMore(false);
      }
    };

    if (page > 0) {
      fetchProducts();
    }

    return () => {
      isMounted = false;
    };
  }, [
    catagory,
    page,
    userInfo?._id,
  ]);

  // =========================================================
  // CATEGORY CHANGE
  // =========================================================

  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setError("");
  }, [catagory]);

  // =========================================================
  // LOAD MORE
  // =========================================================

  const loadMore = () => {
    if (
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      setPage(
        (prev) =>
          prev + 1
      );
    }
  };

  // =========================================================
  // RETRY
  // =========================================================

  const retryLoading = () => {
    setError("");
    setPage(1);
    setProducts([]);
    setHasMore(true);
  };

  // =========================================================
  // GO PRODUCT
  // =========================================================

  const goToProduct = (productId) => {
    if (!productId) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );

      return;
    }

    navigate(
      `/product/${productId}`
    );
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (
    loading &&
    products.length === 0
  ) {
    return (
      <div className="w-full px-2 sm:px-4 md:px-6">
        <div className="flex flex-col items-center justify-center py-16 sm:py-20">
          <div
            className="
              w-14 h-14 sm:w-16 sm:h-16
              rounded-2xl
              bg-blue-50
              dark:bg-blue-500/10
              flex
              items-center
              justify-center
              mb-5
            "
          >
            <div
              className="
                w-7 h-7 sm:w-8 sm:h-8
                border-4
                border-blue-200
                dark:border-blue-900
                border-t-blue-600
                dark:border-t-blue-400
                rounded-full
                animate-spin
              "
            />
          </div>

          <h3
            className="
              text-base
              sm:text-lg
              font-semibold
              text-gray-800
              dark:text-white
              mb-2
            "
          >
            Məhsullar yüklənir...
          </h3>

          <p
            className="
              text-xs
              sm:text-sm
              text-gray-500
              dark:text-gray-400
              text-center
            "
          >
            Zəhmət olmasa, gözləyin.
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN
  // =========================================================

  return (
    <div className="w-full px-2 sm:px-4 md:px-6">

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div
          className="
            mb-5
            rounded-2xl
            border
            border-red-200
            dark:border-red-900/50
            bg-red-50
            dark:bg-red-950/30
            p-4
          "
        >
          <div className="flex items-start gap-3">
            <div
              className="
                flex-shrink-0
                w-10 h-10
                rounded-xl
                bg-red-100
                dark:bg-red-900/40
                text-red-600
                dark:text-red-400
                flex
                items-center
                justify-center
              "
            >
              <FaExclamationTriangle
                size={18}
              />
            </div>

            <div className="flex-1 min-w-0">
              <h3
                className="
                  text-sm
                  sm:text-base
                  font-bold
                  text-red-800
                  dark:text-red-300
                  mb-1
                "
              >
                Xəta baş verdi
              </h3>

              <p
                className="
                  text-xs
                  sm:text-sm
                  text-red-700
                  dark:text-red-400
                  whitespace-pre-line
                  break-words
                "
              >
                {error}
              </p>

              <button
                type="button"
                onClick={
                  retryLoading
                }
                className="
                  mt-3
                  inline-flex
                  items-center
                  gap-2
                  px-4
                  py-2
                  rounded-xl
                  bg-red-600
                  hover:bg-red-700
                  text-white
                  text-xs
                  sm:text-sm
                  font-semibold
                  transition
                "
              >
                <FaSyncAlt
                  size={13}
                />
                Yenidən cəhd et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          PRODUCT GRID
      ===================================================== */}

      {products.length > 0 ? (
        <div
          className="
            grid
            grid-cols-2
            sm:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            xl:grid-cols-5

            gap-2
            sm:gap-4
            lg:gap-5

            w-full
          "
        >
          {products.map(
            (product) => {
              const productId =
                String(
                  product._id
                );

              const isAdding =
                addingProductId ===
                productId;

              const isAddingFavorite =
                addingFavoriteId ===
                productId;

              const isFavorite =
                favoriteIds.includes(
                  productId
                );

              const isInCart =
                isProductInCart(
                  productId
                );

              const isOutOfStock =
                Number(
                  product.stock
                ) <= 0;

              const image =
                Array.isArray(
                  product.photo
                )
                  ? product.photo[0]
                  : product.photo;

              return (
                <div
                  key={
                    product._id
                  }
                  className="
                    group
                    relative
                    min-w-0
                    overflow-hidden

                    rounded-2xl
                    sm:rounded-3xl

                    border
                    border-gray-100
                    dark:border-gray-700

                    bg-white
                    dark:bg-gray-800

                    shadow-sm
                    hover:shadow-xl

                    transition-all
                    duration-300

                    sm:hover:-translate-y-1

                    flex
                    flex-col
                  "
                >

                  {/* =================================================
                      CART STATUS
                  ================================================= */}

                  {isInCart && (
                    <div
                      className="
                        absolute
                        left-2
                        top-2
                        sm:left-3
                        sm:top-3

                        z-10

                        flex
                        items-center
                        gap-1

                        rounded-full

                        bg-emerald-500

                        px-2
                        sm:px-3

                        py-1
                        sm:py-1.5

                        text-[9px]
                        sm:text-xs

                        font-bold
                        text-white

                        shadow-lg
                      "
                    >
                      <FaCheckCircle />

                      <span>
                        Səbətinizdədir
                      </span>
                    </div>
                  )}

                  {/* =================================================
                      HEART
                  ================================================= */}

                  <button
                    type="button"
                    aria-label={
                      isFavorite
                        ? "Sevimlilərdən çıxar"
                        : "Sevimlilərə əlavə et"
                    }
                    onClick={() =>
                      handleAddToFavorite(
                        product
                      )
                    }
                    disabled={
                      isAddingFavorite
                    }
                    className={`
                      absolute
                      z-20

                      top-2
                      right-2

                      sm:top-3
                      sm:right-3

                      w-8
                      h-8

                      sm:w-10
                      sm:h-10

                      rounded-full

                      bg-white/95
                      dark:bg-gray-900/90

                      shadow-md

                      flex
                      items-center
                      justify-center

                      transition-all
                      duration-200

                      ${isAddingFavorite
                        ? "cursor-wait opacity-70"
                        : "hover:scale-105"
                      }

                      ${isFavorite
                        ? "text-red-500"
                        : "text-gray-700 dark:text-gray-200 hover:text-red-500"
                      }
                    `}
                  >
                    {isAddingFavorite ? (
                      <div
                        className="
                          w-4
                          h-4
                          sm:w-5
                          sm:h-5

                          border-2
                          border-gray-300
                          border-t-red-500

                          rounded-full
                          animate-spin
                        "
                      />
                    ) : isFavorite ? (
                      <FaHeart
                        className="
                          w-4
                          h-4
                          sm:w-5
                          sm:h-5
                        "
                      />
                    ) : (
                      <FaRegHeart
                        className="
                          w-4
                          h-4
                          sm:w-5
                          sm:h-5
                        "
                      />
                    )}
                  </button>

                  {/* =================================================
                      IMAGE
                  ================================================= */}

                  <div
                    className="
                      relative
                      w-full

                      aspect-[1/1]

                      bg-gray-50
                      dark:bg-gray-900/40

                      overflow-hidden
                    "
                  >
                    {image ? (
                      <img
                        src={image}
                        alt={
                          product.title ||
                          "Məhsul"
                        }
                        loading="lazy"
                        className="
                          w-full
                          h-full
                          object-cover

                          cursor-pointer

                          transition-transform
                          duration-500

                          group-hover:scale-105
                        "
                        onClick={() =>
                          goToProduct(
                            product._id
                          )
                        }
                      />
                    ) : (
                      <div
                        className="
                          w-full
                          h-full
                          flex
                          items-center
                          justify-center
                          text-gray-400
                          text-xs
                        "
                      >
                        Şəkil yoxdur
                      </div>
                    )}

                    {/* OUT OF STOCK */}

                    {isOutOfStock && (
                      <div
                        className="
                          absolute
                          left-2
                          bottom-2
                          sm:left-3
                          sm:bottom-3

                          px-2
                          sm:px-3
                          py-1

                          rounded-full

                          bg-red-600
                          text-white

                          text-[10px]
                          sm:text-xs

                          font-bold
                        "
                      >
                        Stokda yoxdur
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      CONTENT
                  ================================================= */}

                  <div
                    className="
                      flex
                      flex-col
                      flex-1

                      p-2.5
                      sm:p-4
                    "
                  >

                    {/* TITLE */}

                    <h3
                      onClick={() =>
                        goToProduct(
                          product._id
                        )
                      }
                      className="
                        text-xs
                        sm:text-base

                        font-semibold

                        text-gray-800
                        dark:text-white

                        leading-5
                        sm:leading-6

                        line-clamp-2

                        min-h-[40px]
                        sm:min-h-[48px]

                        mb-1.5
                        sm:mb-2

                        cursor-pointer

                        hover:text-blue-600
                        dark:hover:text-blue-400

                        transition-colors
                      "
                    >
                      {product.title ||
                        "Adsız məhsul"}
                    </h3>

                    {/* PRICE */}

                    <div
                      className="
                        min-h-[24px]

                        flex
                        flex-wrap
                        items-center

                        gap-1

                        mb-1
                      "
                    >
                      {product.discountApplied ? (
                        <>
                          <span
                            className="
                              text-[10px]
                              sm:text-sm

                              text-red-500

                              line-through
                            "
                          >
                            {
                              product.originalPrice
                            }
                            ₼
                          </span>

                          <span
                            className="
                              text-sm
                              sm:text-lg

                              font-bold

                              text-green-600
                            "
                          >
                            {
                              product.price
                            }
                            ₼
                          </span>
                        </>
                      ) : (
                        <span
                          className="
                            text-sm
                            sm:text-lg

                            font-bold

                            text-gray-900
                            dark:text-white
                          "
                        >
                          {
                            product.price
                          }
                          ₼
                        </span>
                      )}
                    </div>

                    {/* STOCK */}

                    <p
                      className={`
                        text-[10px]
                        sm:text-xs

                        font-medium

                        mb-2.5
                        sm:mb-3

                        ${product.stock > 0
                          ? "text-green-600"
                          : "text-red-600"
                        }
                      `}
                    >
                      {product.stock > 0
                        ? `Stokda ${product.stock} ədəd`
                        : "Mövcud deyil"}
                    </p>

                    {/* =================================================
                        CART BUTTON
                    ================================================= */}

                    <button
                      type="button"
                      onClick={() =>
                        handleAddToCart(
                          product
                        )
                      }
                      disabled={
                        isAdding ||
                        Boolean(
                          addingProductId
                        ) ||
                        (
                          isOutOfStock &&
                          !isInCart
                        )
                      }
                      className={`
  mt-auto

  w-full

  min-h-[38px]
  sm:min-h-[44px]

  px-2
  sm:px-3

  py-2
  sm:py-2.5

  rounded-xl
  sm:rounded-2xl

  text-[10px]
  sm:text-sm

  font-semibold

  flex
  items-center
  justify-center

  gap-1
  sm:gap-2

  transition-all

  ${isInCart
                          ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 dark:hover:border-red-800 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                          : isOutOfStock
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : isAdding
                              ? "bg-blue-400 text-white cursor-wait"
                              : "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]"
                        }
`}
                    >
                      {isAdding ? (
                        <>
                          <div
                            className="
                              w-4
                              h-4

                              sm:w-5
                              sm:h-5

                              border-2
                              border-white/40
                              border-t-white

                              rounded-full
                              animate-spin
                            "
                          />

                          <span>
                            {isInCart
                              ? "Səbətdən silinir..."
                              : "Səbətə əlavə edilir..."}
                          </span>
                        </>
                      ) : isInCart ? (
                        <>
                          <FaTrash
                            size={13}
                          />

                          <span className="flex flex-col items-center leading-tight">
                            <span>
                              Səbətinizdədir
                            </span>

                            <span className="text-[9px] sm:text-[10px] font-medium opacity-70">
                              Silmək üçün toxunun
                            </span>
                          </span>
                        </>
                      ) : isOutOfStock ? (
                        <>
                          <FaShoppingCart
                            size={13}
                          />

                          <span>
                            Mövcud deyil
                          </span>
                        </>
                      ) : (
                        <>
                          <FaShoppingCart
                            size={13}
                          />

                          <span>
                            Səbətə əlavə et
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            }
          )}
        </div>
      ) : !error ? (
        /* =====================================================
           NO PRODUCTS
        ===================================================== */

        <div
          className="
            flex
            flex-col
            items-center
            justify-center

            py-14
            sm:py-16

            px-4
          "
        >
          <div
            className="
              w-14
              h-14

              sm:w-16
              sm:h-16

              rounded-2xl

              bg-gray-100
              dark:bg-gray-700

              flex
              items-center
              justify-center

              mb-4
            "
          >
            <FaShoppingCart
              className="
                text-gray-400
                dark:text-gray-500

                text-xl
                sm:text-2xl
              "
            />
          </div>

          <h3
            className="
              text-base
              sm:text-lg

              font-semibold

              text-gray-800
              dark:text-white

              mb-2
            "
          >
            Məhsul tapılmadı
          </h3>

          <p
            className="
              text-xs
              sm:text-sm

              text-gray-500
              dark:text-gray-400

              text-center
            "
          >
            Bu kateqoriyada hazırda heç bir
            məhsul yoxdur.
          </p>
        </div>
      ) : null}

      {/* =====================================================
          LOAD MORE
      ===================================================== */}

      {hasMore &&
        products.length > 0 &&
        !error && (
          <div
            className="
              flex
              justify-center

              mt-6
              sm:mt-8
            "
          >
            <button
              type="button"
              onClick={
                loadMore
              }
              disabled={
                loadingMore
              }
              className="
                w-full
                sm:w-auto

                min-w-0
                sm:min-w-[190px]

                px-5
                sm:px-6

                py-2.5
                sm:py-3

                bg-blue-600
                text-white

                rounded-xl
                sm:rounded-lg

                hover:bg-blue-700

                disabled:bg-blue-400
                disabled:cursor-wait

                transition-colors

                text-sm
                font-semibold

                flex
                items-center
                justify-center
                gap-2
              "
            >
              {loadingMore ? (
                <>
                  <div
                    className="
                      w-4
                      h-4

                      sm:w-5
                      sm:h-5

                      border-2
                      border-white/40
                      border-t-white

                      rounded-full

                      animate-spin
                    "
                  />

                  Daha çox yüklənir...
                </>
              ) : (
                "Daha çox göstər"
              )}
            </button>
          </div>
        )}

      {/* =====================================================
          END
      ===================================================== */}

      {!hasMore &&
        products.length > 0 &&
        !loadingMore &&
        !error && (
          <p
            className="
              text-center

              text-xs
              sm:text-sm

              text-gray-400
              dark:text-gray-500

              mt-6
              sm:mt-8
            "
          >
            Bütün məhsullar göstərilir.
          </p>
        )}
    </div>
  );
};

export default Catos;
